import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { allocateStudents, createClassroom } from "../services/classroom";
import { levelKey } from "../utils/classesLabels";

const currentYear = new Date().getFullYear();
const SHIFT_OPTIONS = ["Matutino", "Vespertino", "Diurno", "Nocturno"];

// Opened from ListaEstudantesLayer's actions dropdown ("Alocar Turma"). Lets
// the operator either (a) allocate the student into an existing Turma that
// matches the student's own Classe/Nível/Curso (`record.corseId`), or
// (b) create that Turma on the spot — pre-locked to the student's Classe —
// and allocate the student to it in the same request (createClassroom
// accepts an embedded `students` array, see services/classroom.js).
//
// `allocateStudents` on the backend fully REPLACES a classroom's student
// list rather than appending, so the "select existing" path always starts
// from that classroom's current roster (already available on the
// `classrooms` prop) and appends the student locally before submitting.
const AllocateClassroomModal = ({ modalId, record, frequencies, classrooms, onDone }) => {
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const [mode, setMode] = useState("select");
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [form, setForm] = useState({ className: "", shift: "Diurno", maxStudents: 25 });
  const [submitting, setSubmitting] = useState(false);

  const studentId = record?.studentId || record?.student?.id;
  const studentName = record?.student?.name || "";
  const studentCode = record?.student?.code || "";
  const academicYear = record?.year || currentYear;

  const classeFreq = (frequencies || []).find(
    (f) => String(f.id) === String(record?.corseId) || f.level === record?.student?.level,
  );
  const classeId = classeFreq?.id || record?.corseId;

  const matchingClassrooms = (classrooms || []).filter(
    (c) => String(c.educationLevel) === String(classeId),
  );
  const alreadyAllocatedIn = matchingClassrooms.find((c) =>
    (c.students || []).some((s) => String(s.id) === String(studentId)),
  );
  const selectableClassrooms = matchingClassrooms.filter((c) => c.id !== alreadyAllocatedIn?.id);

  useEffect(() => {
    setMode("select");
    setSelectedClassroomId("");
    setForm({ className: "", shift: "Diurno", maxStudents: 25 });
  }, [record]);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const closeModal = () => Modal.getInstance(modalRef.current)?.hide();

  const handleSelectConfirm = async () => {
    if (!selectedClassroomId) {
      toast.error(t("turma.error.requiredFields"));
      return;
    }
    setSubmitting(true);
    try {
      const classroom = matchingClassrooms.find((c) => String(c.id) === String(selectedClassroomId));
      const existingStudents = Array.isArray(classroom?.students) ? classroom.students : [];
      const alreadyIn = existingStudents.some((s) => String(s.id) === String(studentId));
      const nextStudents = alreadyIn
        ? existingStudents
        : [...existingStudents, { id: studentId, name: studentName, code: studentCode }];
      await allocateStudents(selectedClassroomId, nextStudents);
      toast.success(t("estudante.allocateTurma.success"));
      onDone?.();
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("estudante.allocateTurma.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateConfirm = async () => {
    if (!form.className.trim() || !classeId || !form.shift) {
      toast.error(t("turma.error.requiredFields"));
      return;
    }
    setSubmitting(true);
    try {
      await createClassroom({
        className: form.className.trim(),
        code: form.className.trim().replace(/\s+/g, "").toUpperCase(),
        academicYear: Number(academicYear),
        educationLevel: classeId,
        shift: form.shift,
        maxStudents: Number(form.maxStudents) || 0,
        students: [{ id: studentId, name: studentName, code: studentCode }],
      });
      toast.success(t("estudante.allocateTurma.success"));
      onDone?.();
      closeModal();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("turma.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='modal fade' id={modalId} tabIndex={-1} aria-hidden='true' ref={modalRef}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content radius-16 bg-base'>
          <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
            <h6 className='mb-0'>{t("estudante.list.action.allocateTurma")}</h6>
            <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close' />
          </div>
          <div className='modal-body p-24'>
            <p className='text-secondary-light mb-4'>{studentName}</p>
            <p className='text-secondary-light text-sm mb-16'>
              {t(levelKey("turma.field.educationLevel"))}: <strong>{classeFreq?.description || "-"}</strong>
            </p>

            <div className='d-flex gap-2 mb-16'>
              <button
                type='button'
                className={`btn btn-sm px-16 ${mode === "select" ? "btn-primary-600" : "btn-outline-primary"}`}
                onClick={() => setMode("select")}
              >
                {t("estudante.allocateTurma.tab.select")}
              </button>
              <button
                type='button'
                className={`btn btn-sm px-16 ${mode === "create" ? "btn-primary-600" : "btn-outline-primary"}`}
                onClick={() => setMode("create")}
              >
                {t("estudante.allocateTurma.tab.create")}
              </button>
            </div>

            {mode === "select" ? (
              selectableClassrooms.length === 0 ? (
                <p className='text-secondary-light mb-0'>{t("estudante.allocateTurma.select.empty")}</p>
              ) : (
                <>
                  {alreadyAllocatedIn && (
                    <div className='alert alert-info d-flex align-items-center gap-2 mb-12 py-8 text-sm'>
                      <Icon icon='mdi:information-outline' />
                      {t("estudante.allocateTurma.select.alreadyAllocated")} ({alreadyAllocatedIn.className})
                    </div>
                  )}
                  <select
                    className='form-select'
                    value={selectedClassroomId}
                    onChange={(e) => setSelectedClassroomId(e.target.value)}
                  >
                    <option value=''>{t("estudante.allocateTurma.select.placeholder")}</option>
                    {selectableClassrooms.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.className} {c.shift ? `· ${c.shift}` : ""}
                      </option>
                    ))}
                  </select>
                </>
              )
            ) : (
              <div className='row gy-3'>
                <div className='col-sm-12'>
                  <label className='form-label text-sm mb-4'>{t("turma.field.className")}</label>
                  <input
                    type='text'
                    className='form-control'
                    value={form.className}
                    onChange={setField("className")}
                    autoFocus
                  />
                </div>
                <div className='col-sm-6'>
                  <label className='form-label text-sm mb-4'>{t(levelKey("turma.field.educationLevel"))}</label>
                  <input type='text' className='form-control' value={classeFreq?.description || ""} disabled />
                </div>
                <div className='col-sm-6'>
                  <label className='form-label text-sm mb-4'>{t("turma.field.shift")}</label>
                  <select className='form-select' value={form.shift} onChange={setField("shift")}>
                    {SHIFT_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label text-sm mb-4'>{t("turma.field.maxStudents")}</label>
                  <input
                    type='number'
                    min='1'
                    className='form-control'
                    value={form.maxStudents}
                    onChange={setField("maxStudents")}
                  />
                </div>
              </div>
            )}
          </div>
          <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
            <button type='button' className='btn btn-neutral-500 border-neutral-100 px-24' data-bs-dismiss='modal'>
              {t("global.cancel")}
            </button>
            <button
              type='button'
              className='btn btn-primary-600 px-24 d-flex align-items-center gap-2'
              onClick={mode === "select" ? handleSelectConfirm : handleCreateConfirm}
              disabled={
                submitting ||
                (mode === "select" && selectableClassrooms.length === 0) ||
                (mode === "create" && !classeId)
              }
            >
              {submitting && <Icon icon='svg-spinners:ring-resize' />}
              {t("global.confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllocateClassroomModal;
